import { useEffect, useRef } from "react";
import { useReducedMotion } from "framer-motion";
import { InkReveal } from "./InkReveal";
import { isLowPower, isSlowConnection } from "../utils/perf";

// Small looping "satisfying tech" video panel. Muted, inline; the source is
// only attached when the panel approaches the viewport (preload="none"), it
// pauses off-screen, and slow/data-saver connections never download it.
export const TechVideo = ({ src, label, className = "", testid }) => {
  const reduce = useReducedMotion();
  const low = isLowPower();
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    el.muted = true;
    el.setAttribute("muted", "");
    if (reduce || low || isSlowConnection()) return undefined;
    const tryPlay = () => el.play().catch(() => {});
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (!el.src) el.src = src; // download starts only when near view
          tryPlay();
        } else {
          el.pause();
        }
      },
      { rootMargin: "300px", threshold: 0.01 }
    );
    io.observe(el);
    el.addEventListener("canplay", tryPlay, { once: true });
    return () => io.disconnect();
  }, [reduce, low, src]);

  return (
    <div data-testid={testid} className={`group/vid relative ${className}`}>
      <InkReveal className="absolute inset-0">
        <video
          ref={ref}
          muted
          loop
          playsInline
          preload="none"
          aria-hidden="true"
          disablePictureInPicture
          disableRemotePlayback
          controlsList="nodownload noplaybackrate noremoteplayback"
          className="h-full w-full object-cover transition-transform duration-700 group-hover/vid:scale-[1.04]"
        />
      </InkReveal>
    </div>
  );
};
